import React, { useRef, useEffect, useState, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { ImageAnnotation, BoundingBox, Polygon, Point } from '../../types/training';
import {
  addBoundingBox,
  addPolygon,
  removeAnnotation,
  clearAllAnnotations,
  drawAnnotationsOnCanvas,
  isBoundingBox,
  isPolygon,
} from '../../utils/annotationUtils';
import './ImageAnnotationEditor.css';

interface ImageAnnotationEditorProps {
  imageSrc: string;
  annotations?: ImageAnnotation[];
  onAnnotationsChange: (annotations: ImageAnnotation[]) => void;
  selectedColor: string;
  isEditMode: boolean;
  selectedTool?: 'bbox' | 'polygon';
  onAddAnnotation?: (annotation: ImageAnnotation) => void;
  onRemoveAnnotation?: (annotationId: string) => void;
}

const ImageAnnotationEditor: React.FC<ImageAnnotationEditorProps> = ({
  imageSrc,
  annotations = [],
  onAnnotationsChange,
  selectedColor,
  isEditMode,
  selectedTool = 'bbox',
  onAddAnnotation,
  onRemoveAnnotation,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);

  const [isDrawing, setIsDrawing] = useState(false);
  const [startPoint, setStartPoint] = useState<Point | null>(null);
  const [currentPoints, setCurrentPoints] = useState<Point[]>([]);
  const [scale, setScale] = useState(1);

  // Draw annotations on canvas
  const redrawCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    const image = imageRef.current;

    if (!canvas || !image || !image.complete) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Draw image
    ctx.drawImage(image, 0, 0, canvas.width, canvas.height);

    // Draw annotations
    drawAnnotationsOnCanvas(ctx, annotations, 1);

    // Draw current drawing (for bbox)
    if (isDrawing && startPoint && selectedTool === 'bbox') {
      const mousePos = getMousePos(canvas, new MouseEvent(''));
      if (mousePos) {
        ctx.strokeStyle = selectedColor;
        ctx.lineWidth = 2;
        ctx.strokeRect(
          startPoint.x,
          startPoint.y,
          mousePos.x - startPoint.x,
          mousePos.y - startPoint.y
        );
      }
    }

    // Draw current polygon being drawn
    if (currentPoints.length > 0 && selectedTool === 'polygon') {
      ctx.strokeStyle = selectedColor;
      ctx.lineWidth = 2;
      ctx.globalAlpha = 0.8;

      if (currentPoints.length > 0) {
        ctx.beginPath();
        ctx.moveTo(currentPoints[0].x, currentPoints[0].y);
        for (let i = 1; i < currentPoints.length; i++) {
          ctx.lineTo(currentPoints[i].x, currentPoints[i].y);
        }
        if (isDrawing) {
          const canvas = canvasRef.current;
          if (canvas) {
            const rect = canvas.getBoundingClientRect();
            const mouseEvent = new MouseEvent('');
            const mousePos = getMousePos(canvas, mouseEvent);
            if (mousePos) {
              ctx.lineTo(mousePos.x, mousePos.y);
            }
          }
        }
        ctx.stroke();
      }

      // Draw points
      ctx.fillStyle = selectedColor;
      for (const point of currentPoints) {
        ctx.beginPath();
        ctx.arc(point.x, point.y, 5, 0, 2 * Math.PI);
        ctx.fill();
      }

      ctx.globalAlpha = 1;
    }
  }, [annotations, isDrawing, startPoint, selectedTool, selectedColor, currentPoints]);

  // Get mouse position relative to canvas
  const getMousePos = (
    canvas: HTMLCanvasElement,
    evt: MouseEvent | Touch
  ): Point | null => {
    const rect = canvas.getBoundingClientRect();
    return {
      x: ((evt as any).clientX || (evt as any).pageX - rect.left - window.scrollX) || 0,
      y: ((evt as any).clientY || (evt as any).pageY - rect.top - window.scrollY) || 0,
    };
  };

  // Handle image load
  const handleImageLoad = useCallback(() => {
    const canvas = canvasRef.current;
    const image = imageRef.current;

    if (!canvas || !image) return;

    // Set canvas size to match image
    canvas.width = image.naturalWidth;
    canvas.height = image.naturalHeight;

    // Fit canvas to container
    if (containerRef.current) {
      const containerWidth = containerRef.current.clientWidth;
      const containerHeight = containerRef.current.clientHeight - 100; // Leave space for controls
      const scaleX = containerWidth / canvas.width;
      const scaleY = containerHeight / canvas.height;
      const newScale = Math.min(scaleX, scaleY, 1);

      setScale(newScale);
      canvas.style.maxWidth = (canvas.width * newScale) + 'px';
      canvas.style.maxHeight = (canvas.height * newScale) + 'px';
    }

    redrawCanvas();
  }, [redrawCanvas]);

  // Handle canvas mouse down
  const handleMouseDown = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      if (!isEditMode) return;

      const canvas = canvasRef.current;
      if (!canvas) return;

      const pos = getMousePos(canvas, e.nativeEvent);
      if (!pos) return;

      if (selectedTool === 'bbox') {
        setIsDrawing(true);
        setStartPoint(pos);
      } else if (selectedTool === 'polygon') {
        setCurrentPoints([...currentPoints, pos]);
      }
    },
    [isEditMode, selectedTool, currentPoints]
  );

  // Handle canvas mouse move
  const handleMouseMove = useCallback(() => {
    redrawCanvas();
  }, [redrawCanvas]);

  // Handle canvas mouse up
  const handleMouseUp = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      if (!isEditMode) return;

      const canvas = canvasRef.current;
      if (!canvas) return;

      const endPoint = getMousePos(canvas, e.nativeEvent);
      if (!endPoint || !startPoint) return;

      if (selectedTool === 'bbox' && isDrawing) {
        setIsDrawing(false);

        const x = Math.min(startPoint.x, endPoint.x);
        const y = Math.min(startPoint.y, endPoint.y);
        const width = Math.abs(endPoint.x - startPoint.x);
        const height = Math.abs(endPoint.y - startPoint.y);

        if (width > 5 && height > 5) {
          // Minimum size threshold
          const newAnnotation = {
            id: uuidv4(),
            x,
            y,
            width,
            height,
            color: selectedColor,
            createdAt: Date.now(),
          } as BoundingBox;

          onAnnotationsChange([...annotations, newAnnotation]);
          if (onAddAnnotation) {
            onAddAnnotation(newAnnotation);
          }
        }

        setStartPoint(null);
      }
    },
    [isEditMode, selectedTool, isDrawing, startPoint, selectedColor, annotations, onAnnotationsChange, onAddAnnotation]
  );

  // Handle polygon completion (right click or double click)
  const handleContextMenu = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      e.preventDefault();
      if (selectedTool === 'polygon' && currentPoints.length > 2) {
        const newAnnotation = {
          id: uuidv4(),
          points: currentPoints,
          color: selectedColor,
          createdAt: Date.now(),
        } as Polygon;

        onAnnotationsChange([...annotations, newAnnotation]);
        if (onAddAnnotation) {
          onAddAnnotation(newAnnotation);
        }

        setCurrentPoints([]);
      }
    },
    [selectedTool, currentPoints, selectedColor, annotations, onAnnotationsChange, onAddAnnotation]
  );

  // Handle removing annotation
  const handleRemoveAnnotation = useCallback(
    (annotationId: string) => {
      if (onRemoveAnnotation) {
        onRemoveAnnotation(annotationId);
      }
      onAnnotationsChange(removeAnnotation(annotations, annotationId));
    },
    [annotations, onAnnotationsChange, onRemoveAnnotation]
  );

  // Handle clearing all annotations
  const handleClearAll = useCallback(() => {
    onAnnotationsChange(clearAllAnnotations());
    setCurrentPoints([]);
  }, [onAnnotationsChange]);

  // Handle canceling polygon
  const handleCancelPolygon = useCallback(() => {
    setCurrentPoints([]);
  }, []);

  // Redraw canvas when annotations or drawing state changes
  useEffect(() => {
    redrawCanvas();
  }, [redrawCanvas]);

  return (
    <div className="image-annotation-editor">
      <div className="canvas-container" ref={containerRef}>
        {imageSrc && (
          <img
            ref={imageRef}
            src={imageSrc}
            alt="Image for annotation"
            style={{ display: 'none' }}
            onLoad={handleImageLoad}
          />
        )}
        <canvas
          ref={canvasRef}
          className={`annotation-canvas ${isEditMode ? 'edit-mode' : 'view-mode'}`}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={() => {
            if (isDrawing) setIsDrawing(false);
          }}
          onContextMenu={handleContextMenu}
        />
        {selectedTool === 'polygon' && currentPoints.length > 0 && isEditMode && (
          <div className="polygon-hint">
            <p>Right-click to finish polygon</p>
            <button onClick={handleCancelPolygon} className="cancel-btn">
              Cancel
            </button>
          </div>
        )}
      </div>

      {/* Annotations List */}
      {annotations && annotations.length > 0 && (
        <div className="annotations-list-section">
          <div className="annotations-list-header">
            <h4>Annotations ({annotations.length})</h4>
          </div>
          <div className="annotations-list">
            {annotations.map((ann) => (
              <div key={ann.id} className="annotation-item">
                <div
                  className="annotation-color-indicator"
                  style={{ backgroundColor: ann.color }}
                />
                <div className="annotation-content">
                  <div className="annotation-type">
                    {isBoundingBox(ann) ? '📦 Bounding Box' : '🔷 Polygon'}
                  </div>
                  <div className="annotation-details">
                    {isBoundingBox(ann)
                      ? `(${ann.x.toFixed(0)}, ${ann.y.toFixed(0)}) ${ann.width.toFixed(0)}x${ann.height.toFixed(0)}`
                      : `${ann.points.length} points`}
                  </div>
                </div>
                {isEditMode && (
                  <button
                    className="remove-annotation-btn"
                    onClick={() => handleRemoveAnnotation(ann.id)}
                    title="Remove annotation"
                  >
                    ✕
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty State */}
      {(!annotations || annotations.length === 0) && !currentPoints.length && (
        <div className="empty-state">
          {!imageSrc ? (
            <p>⚠️ No image data available. Please ensure the image file was uploaded correctly.</p>
          ) : isEditMode ? (
            <>
              <p>
                {selectedTool === 'bbox'
                  ? '🖱️ Click and drag to draw bounding boxes'
                  : '🖱️ Click to add polygon points (right-click to finish)'}
              </p>
              <p className="secondary">Choose a tool and color from the toolbar</p>
            </>
          ) : (
            <p>No annotations in this image</p>
          )}
        </div>
      )}
    </div>
  );
};

export default ImageAnnotationEditor;
